export default defineNuxtPlugin(nuxtApp => {
	if (import.meta.server) {
		nuxtApp.vueApp.directive('interpolation', {});
		return;
	}

	const config = useAppConfig();
	const {langs, get: getLanguage} = useLang();
	const modal = useModal();
	const page = usePage();
	const {emit} = useEventBus();
	const router = nuxtApp.$router;
	const sHost = nuxtApp.$host;
	const managedScriptSelector = 'script:not([data-executed]):not([data-key^="dynamic-script-"]):not([data-key^="inline-script-"])';

	// Handle supported links through one delegated listener.
	async function navigate(e) {
		if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

		// use event delegation - check if clicked element is a link or inside a link
		const link = e.target?.closest?.('a');
		if (!link) return;

		// check if the link should be handled
		if (!shouldHandleLink(link)) return;

		const item = link;
		const url = new URL(link.href);
		if (url.protocol !== 'http:' && url.protocol !== 'https:') return;

		// link with target="_blank" - allow natural browser behavior
		if (link.getAttribute('target') === '_blank') {
			const rel = link.getAttribute('rel') || '';
			if (!rel.split(/\s+/).includes('noopener')) link.setAttribute('rel', `${rel} noopener`.trim());
			return;
		}

		const mode = url.searchParams.get('mode');
		const linkClass = link.getAttribute('class');

		// close modals if clicked link is inside a modal
		if (Object.keys(modal.activeModals()).length > 0 && mode != 'quick') {
			modal.close();
		}

		// if link has ?mode parameter, open modal defined in parameter
		// if mode is 'quick' then fetch page from the link url and open 'quick' modal
		if (mode) {
			e.preventDefault();
			const pathname = url.pathname;

			// if mode is 'quick' then fetch page and open modal
			if (mode == 'quick') {
				const res = await page.fetch({'slug': pathname});
				if (!res) return null;
				modal.open('quick', res);
				return res;
			}

			// open custom modal
			modal.open(mode);
			return;
		}

		// if link has class 'fancybox' then open modal with gallery
		if (linkClass?.includes('fancybox')) {
			e.preventDefault();
			const gallery = link.getAttribute('rel');
			let items = [];
			const startIndex = link.getAttribute('data-index') ? link.getAttribute('data-index') : 0;

			if (gallery) {
				// if rel attribute is defined, get all elements with rel set to the same gallery
				const galleryElements = document.querySelectorAll(`a[rel="${gallery}"]`);
				galleryElements.forEach((item, i) => {
					if (item.href) {
						const img = item.querySelector('img');
						items.push({
							id: `item-${i}`,
							title: img?.alt ? img.alt : item.title,
							url: item.href,
							thumb: item.getAttribute('data-thumb'),
							type: item.href.includes('youtube.com') ? 'youtube' : 'image',
						});
					}
				});
			} else {
				// if rel attribute is not defined, get only the current link
				if (link.href) {
					const img = link.querySelector('img');
					items.push({
						id: 'item-0',
						title: img?.alt ? img.alt : link.title,
						url: link.href,
						thumb: link.getAttribute('data-thumb'),
						type: link.href.includes('youtube.com') ? 'youtube' : 'image',
					});
				}
			}

			// open modal with name defined in data-modal attribute. If not defined, open 'quick' modal
			const modalName = link.getAttribute('data-modal') ? link.getAttribute('data-modal') : 'quick';
			modal.open(modalName, {
				'items': items,
				'startIndex': Number(startIndex),
			});
			return;
		}

		// internal link
		if (item.origin === sHost || item.host === window.location.host) {
			// A language change must reinitialise the app and its language-specific data.
			if (config.multilanguage) {
				const defaultLanguage = config.multiHost?.find(host => window.location.host.includes(host.alias))?.lang || config.lang;
				const languageSegment = url.pathname.split('/').filter(Boolean)[0];
				const targetLanguage = langs[languageSegment]?.code || defaultLanguage;
				if (targetLanguage !== getLanguage()) return;
			}

			// Keep native fragment scrolling, including the catch-all page's virtual anchor.
			// Modal and gallery actions above still take precedence.
			if (link.getAttribute('href').trim().startsWith('#')) return;

			e.preventDefault();
			if (router?.currentRoute?.value?.path === url.pathname) emit('catalogProductsUpdate');
			const route = url.href.replace(item.origin, ''); // remove origin from url but keep query params
			router.push(route);
		}
	}

	// check if link should be handled by the global navigation listener
	function shouldHandleLink(link) {
		// check if the link has an href attribute
		if (!link.hasAttribute('href')) {
			return false;
		}
		if (link.hasAttribute('download')) return false;

		// ignore links with file extension (except fancybox)
		const href = link.getAttribute('href');
		if (/\.[^/\s]+$/.test(href) && !link.classList.contains('fancybox')) {
			return false;
		}

		return true;
	}

	// wrap each .table element with a div.table-wrapper
	function wrapTables(root = document) {
		const allTables = [];
		if (root?.matches?.('.table')) allTables.push(root);
		if (root?.querySelectorAll) allTables.push(...root.querySelectorAll('.table'));
		if (!allTables?.length) return;
		const tables = [...allTables].filter(function (table) {
			return table.parentNode && !table.parentNode.classList.contains('table-wrapper');
		});

		if (tables.length > 0) {
			for (let i = 0; i < tables.length; i++) {
				if (tables[i].parentNode && !tables[i].parentNode.classList.contains('table-wrapper')) {
					const tableWrapper = document.createElement('div');
					tableWrapper.classList.add('table-wrapper');
					tables[i].parentNode.insertBefore(tableWrapper, tables[i]);
					tableWrapper.appendChild(tables[i]);
				}
			}
		}
	}

	// load and execute external scripts from dynamic content
	let scriptTimeouts = [];
	let isAppHydrated = false;

	if (router) {
		router.afterEach((to, from) => {
			if (from.name !== undefined) isAppHydrated = true;
		});
	}

	function loadExternalScripts(el, isInitialMount = false) {
		const scripts = [];
		if (el?.matches?.(managedScriptSelector)) scripts.push(el);
		if (el?.querySelectorAll) scripts.push(...el.querySelectorAll(managedScriptSelector));
		if (!scripts?.length) return;

		const {addScript} = useMeta();

		scripts.forEach(script => {
			// handle external scripts with src attribute
			if (script.src) {
				// create hash of src URL to avoid special characters in key
				const srcHash = btoa(script.src).replace(/[^a-zA-Z0-9]/g, '');
				const scriptKey = `dynamic-script-${srcHash}`;

				// check if script with same key is already loaded to avoid duplicates
				const existingScript = document.querySelector(`script[data-key="${scriptKey}"]`);
				if (existingScript) {
					script.remove();
					return;
				}

				// create placeholder div element at script location
				const placeholder = document.createElement('div');
				placeholder.setAttribute('data-script-placeholder', scriptKey);
				placeholder.style.display = 'contents'; // doesn't affect layout

				// replace script with placeholder
				script.parentNode.insertBefore(placeholder, script);

				// prepare script configuration for addScript
				const scriptConfig = {
					key: scriptKey,
					src: script.src,
					type: script.type || 'text/javascript',
					appendTo: `[data-script-placeholder="${scriptKey}"]`,
				};

				// copy other attributes if present
				Array.from(script.attributes).forEach(attr => {
					if (attr.name !== 'src' && attr.name !== 'type') {
						if (attr.name.startsWith('data-')) {
							if (!scriptConfig.dataAttributes) scriptConfig.dataAttributes = {};
							scriptConfig.dataAttributes[attr.name.replace('data-', '')] = attr.value;
						} else {
							scriptConfig[attr.name] = attr.value;
						}
					}
				});

				// remove original script element
				script.remove();

				// load script using addScript with delay to ensure DOM is ready
				const timeoutId = setTimeout(() => {
					if (placeholder.isConnected) {
						nextTick(() => {
							addScript(scriptConfig);
						});
					}

					// remove timeout from array after execution
					const index = scriptTimeouts.indexOf(timeoutId);
					if (index > -1) scriptTimeouts.splice(index, 1);
				}, 500);

				// store timeout ID for cleanup
				scriptTimeouts.push(timeoutId);
			}
			// handle inline scripts (scripts without src attribute)
			// skip inline scripts on initial SSR mount to avoid double execution
			else if (script.textContent && !isInitialMount) {
				// create unique hash from script content to avoid duplicates
				const contentHash = btoa(script.textContent.substring(0, 100)).replace(/[^a-zA-Z0-9]/g, '');
				const scriptKey = `inline-script-${contentHash}`;

				// check if this inline script was already executed on this element
				if (script.hasAttribute('data-executed')) {
					script.remove();
					return;
				}

				const scriptContent = script.textContent;
				const scriptType = script.type || 'text/javascript';
				const scriptAttributes = Array.from(script.attributes);
				const parentNode = script.parentNode;

				// mark as executed before removing
				script.setAttribute('data-executed', 'true');

				// remove original script element
				script.remove();

				// execute inline script with delay to ensure DOM and external scripts are ready
				// longer delay than external scripts to ensure they are loaded first
				const timeoutId = setTimeout(() => {
					if (parentNode?.isConnected) {
						const newScript = document.createElement('script');
						newScript.type = scriptType;
						newScript.textContent = scriptContent;
						newScript.setAttribute('data-key', scriptKey);
						newScript.setAttribute('data-executed', 'true');

						// copy other attributes if present
						scriptAttributes.forEach(attr => {
							if (attr.name !== 'type' && attr.name !== 'data-executed') {
								newScript.setAttribute(attr.name, attr.value);
							}
						});

						// append new script to trigger execution
						parentNode.appendChild(newScript);
					}

					// remove timeout from array after execution
					const index = scriptTimeouts.indexOf(timeoutId);
					if (index > -1) scriptTimeouts.splice(index, 1);
				}, 1500);

				// store timeout ID for cleanup
				scriptTimeouts.push(timeoutId);
			}
		});
	}

	let contentFrame;
	const pendingRoots = new Set();

	function processContent(root, isInitialMount = false) {
		wrapTables(root);
		loadExternalScripts(root, isInitialMount);
	}

	function scheduleContentProcessing(root) {
		pendingRoots.add(root);
		if (contentFrame) return;

		contentFrame = requestAnimationFrame(() => {
			pendingRoots.forEach(item => processContent(item, false));
			pendingRoots.clear();
			contentFrame = null;
		});
	}

	// One listener handles existing and future links, including links rendered after route changes.
	document.addEventListener('click', navigate, false);

	// Existing directives remain the explicit boundary for trusted CMS scripts and tables.
	// Link navigation is handled globally and does not add work for each directive instance.
	nuxtApp.vueApp.directive('interpolation', {
		mounted(el) {
			processContent(el, !isAppHydrated);
		},
		updated(el) {
			scheduleContentProcessing(el);
		},
		beforeUnmount(el) {
			pendingRoots.delete(el);
		},
	});

	if (import.meta.hot) {
		import.meta.hot.dispose(() => {
			document.removeEventListener('click', navigate, false);
			cancelAnimationFrame(contentFrame);
			scriptTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
		});
	}
});
